// Local-only Settings repository implementation using localforage
// Settings are persisted via localforage for consistency with other data.
// Sync signaling still uses localStorage for cross-tab broadcasts.

import { SettingsRepository, Settings } from './types';
import {
  getItem,
  setItem,
  removeItem,
  broadcastStorageChange,
  dispatchStorageSignal,
} from '@/lib/storage';
import { safeGetItem } from '@/lib/browserStorage';
import { SETTINGS_SYNC_KEY, LANG_SYNC_KEY } from '@/utils/i18n';

const SETTINGS_KEY = 'settings:v2';
const SETTINGS_SIGNAL_KEY = 'wsc:settings:updated';

class LocalSettingsRepository implements SettingsRepository {
  async get(): Promise<Settings> {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      // Try to load from localforage first (migrated data)
      const stored = await getItem<Settings>(SETTINGS_KEY);
      if (stored) {
        return stored;
      }

      // Check for legacy localStorage data and migrate
      const legacyRaw = safeGetItem('wsc:settings:v1');
      if (legacyRaw) {
        try {
          const legacy = JSON.parse(legacyRaw) as Partial<Settings> | null;
          const migrated: Settings = {
            defaultRegion: legacy?.defaultRegion,
          };
          // Save migrated data to localforage
          await setItem(SETTINGS_KEY, migrated);
          // Clean up old localStorage key
          try {
            window.localStorage.removeItem('wsc:settings:v1');
          } catch (e) {
            // ignore
          }
          return migrated;
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('LocalSettingsRepository.get: failed to parse legacy settings', e);
          }
        }
      }

      return {};
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('LocalSettingsRepository.get: failed to read settings', e);
      }
      return {};
    }
  }

  async save(settings: Settings): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      await setItem(SETTINGS_KEY, settings);
      // Broadcast change for cross-tab sync
      broadcastStorageChange(SETTINGS_SIGNAL_KEY);
      dispatchStorageSignal(SETTINGS_SIGNAL_KEY);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('LocalSettingsRepository.save: failed to write settings', e);
      }
      throw e;
    }
  }

  async clear(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      // Remove both current and legacy keys
      await removeItem(SETTINGS_KEY);
      try {
        window.localStorage.removeItem('wsc:settings:v1');
      } catch (e) {
        // ignore
      }
      // Broadcast change for cross-tab sync
      broadcastStorageChange(SETTINGS_SIGNAL_KEY);
      dispatchStorageSignal(SETTINGS_SIGNAL_KEY);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('LocalSettingsRepository.clear: failed to clear settings', e);
      }
      throw e;
    }
  }

  subscribe(callback: (settings: Settings) => void): () => void {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handler = () => {
      (async () => {
        const next = await this.get();
        callback(next);
      })();
    };

    // Listen for cross-tab storage events (from other tabs)
    const storageHandler = (e: StorageEvent) => {
      if (e.key !== SETTINGS_SYNC_KEY && e.key !== LANG_SYNC_KEY) {
        return;
      }
      handler();
    };

    // Listen for same-tab custom events
    const customEventHandler = () => {
      handler();
    };

    window.addEventListener('storage', storageHandler);
    window.addEventListener(SETTINGS_SIGNAL_KEY, customEventHandler);

    return () => {
      window.removeEventListener('storage', storageHandler);
      window.removeEventListener(SETTINGS_SIGNAL_KEY, customEventHandler);
    };
  }
}

// Export singleton instance
export const localSettingsRepository = new LocalSettingsRepository();
